<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import BPage from 'src/shared/components/base/BPage/BPage.vue';
import SchoolFiltersBar from 'src/modules/School/components/SchoolFiltersBar/SchoolFiltersBar.vue';
import {
  useSchoolData,
  type SchoolClassSummary,
} from 'src/modules/School/composables/useSchoolData';
import { useAuth } from 'src/boot/auth';
import { useSchoolFiltersStore } from 'src/stores/schoolFilters';

const { currentUser } = useAuth();
const router = useRouter();
const schoolFiltersStore = useSchoolFiltersStore();
const { loading, fetchTeacherClasses } = useSchoolData();
const classes = ref<SchoolClassSummary[]>([]);
const classSearchTerm = ref<string>('');
const latestFetchRequestId = ref<number>(0);

const hasSelectedSemester = computed(
  () => !!schoolFiltersStore.selectedSemesterDocumentId,
);

const filteredClasses = computed(() => {
  return classes.value.filter((schoolClass) => {
    const query = classSearchTerm.value.trim().toLowerCase();
    if (!query) return true;

    const name = `${schoolClass.name || ''}`.toLowerCase();
    const code = `${schoolClass.code || ''}`.toLowerCase();
    return name.includes(query) || code.includes(query);
  });
});

function openClassDetail(schoolClass: SchoolClassSummary) {
  const classSchoolDocumentId = schoolClass.school?.documentId || '';
  const semesterDocumentId = schoolFiltersStore.selectedSemesterDocumentId;

  // Carry the class's school + selected semester in the query so the drawer adopts
  // them on arrival. Mutating the store *before* navigating would trigger the
  // filter watcher's router.replace and cancel this push (the old "click twice"
  // bug when no school was selected); navigating with the query opens the class —
  // and selects its school — in a single click.
  void router.push({
    name: 'school.class.detail',
    params: { documentId: schoolClass.documentId },
    query: {
      ...(classSchoolDocumentId ? { school: classSchoolDocumentId } : {}),
      ...(semesterDocumentId ? { semester: semesterDocumentId } : {}),
    },
  });
}

watch(
  [
    () => currentUser.value?.documentId,
    () => schoolFiltersStore.selectedSemesterDocumentId,
    () => schoolFiltersStore.selectedSchoolDocumentId,
  ],
  async ([teacherDocumentId, semesterDocumentId, schoolDocumentId]) => {
    const requestId = latestFetchRequestId.value + 1;
    latestFetchRequestId.value = requestId;

    // A semester must be selected to view classes.
    if (!semesterDocumentId) {
      classes.value = [];
      return;
    }

    const result = await fetchTeacherClasses(
      teacherDocumentId || '',
      semesterDocumentId,
      schoolDocumentId || undefined,
    );

    if (requestId !== latestFetchRequestId.value) return;
    classes.value = result;
  },
  { immediate: true },
);
</script>

<template>
  <BPage>
    <div class="q-pa-md column q-gutter-md school-teacher-home-container">
      <SchoolFiltersBar v-model:search-term="classSearchTerm" />

      <q-inner-loading :showing="loading">
        <q-spinner-gears
          size="60px"
          color="primary"
        />
      </q-inner-loading>

      <q-banner
        v-if="!hasSelectedSemester"
        :class="$q.dark.isActive ? 'bg-grey-9 text-white' : 'bg-grey-3 text-dark'"
      >
        {{ $t('SCHOOL_SELECT_SEMESTER_TO_VIEW') }}
      </q-banner>

      <q-banner
        v-else-if="!filteredClasses.length"
        :class="$q.dark.isActive ? 'bg-grey-9 text-white' : 'bg-grey-3 text-dark'"
      >
        {{ $t('SCHOOL_NO_CLASSES_ASSIGNED_IN_SEMESTER') }}
      </q-banner>

      <div class="q-mt-sm">
        <div class="row q-col-gutter-md">
          <div
            v-for="schoolClass in filteredClasses"
            :key="schoolClass.documentId"
            class="col-12 col-md-6"
          >
            <q-card
              bordered
              class="cursor-pointer"
              @click="openClassDetail(schoolClass)"
            >
              <q-card-section>
                <div class="text-h6">{{ schoolClass.name }}</div>
                <div class="text-caption text-muted">
                  {{ schoolClass.code }}
                </div>
                <q-chip
                  color="primary"
                  text-color="dark"
                  class="q-mt-sm"
                >
                  {{ $t('SCHOOL_STUDENTS_COUNT') }}:
                  {{ schoolClass.enrollments?.length || 0 }}
                </q-chip>
              </q-card-section>
            </q-card>
          </div>
        </div>
      </div>
    </div>
  </BPage>
</template>

<style scoped>
.school-teacher-home-container {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}
</style>
